import {
  sceneObjectTypeError,
  sceneObjectExtensions,
  objectBaseSource,
  sceneObjectTypeName,
} from "../../../util/src/layout/scene-object-types";
/**
 * Builds the list of draggable reference items for the right-hand
 * SceneCatalogNodeList panel. Each catalog entry maps to exactly one
 * palette item that, when dropped on the canvas, creates a pre-configured
 * behavior node pointing to the entry.
 */
import type {
  BehaviorNode,
  BehaviorDraggedNodeTemplate,
  CustomSceneEventNodeTemplate,
  FunctionCallNodeTemplate,
  SetVariableNodeTemplate,
  VariableRefNodeTemplate,
} from "../../../util/src/blueprint/types.js";
import type { SceneBehaviorCatalog } from "../display-editor/layout-types";
import type { SceneBehaviorRoute } from "./SceneBehaviorNavigator.js";
import {
  DEFAULT_SCENE_ANIMATION_NAMES,
  filterValidSceneAnimationEntries,
} from "../../../util/src/blueprint/runtime/scene-animations.js";
import { isCustomEventTriggerGraphPublishable } from "../../../util/src/blueprint/trigger-graph.js";
import { isFunctionGraphPublishable } from "../../../util/src/blueprint/function-graph.js";
import { isVariableGraphPublishable } from "../../../util/src/blueprint/variable-graph.js";
import { getValueTypeColor } from "../../../util/src/blueprint/method.js";
import dico from "../../../util/src/language/dico.js";
import type {
  BehaviorTemplateCatalogItem,
  BehaviorTemplateCatalogSection,
} from "./BehaviorTemplateCatalog.js";

export type SceneCatalogReferenceSection =
  | "animations"
  | "events"
  | "functions"
  | "variables"
  | "objects";

export interface SceneCatalogReferenceItem {
  id: string;
  label: string;
  section: SceneCatalogReferenceSection;
  /** Color category for the dot indicator */
  dotType: string;
  template: BehaviorDraggedNodeTemplate;
}

/** Optional filters applied when building the scene reference catalog. */
export type SceneCatalogReferenceFilter = {
  /** Custom event ID to omit (prevents self-reference in trigger graphs). */
  excludeCustomEventId?: string;
  editingObjectTypeId?: string;
  valueOnly?: boolean;
};

/**
 * Returns a flat list of reference items for the scene catalog right palette.
 * Animations include built-in presets; custom animations with the same name
 * (case-insensitive) replace the preset.
 */
export function getSceneCatalogReferenceCatalog(
  catalog: SceneBehaviorCatalog | undefined,
  filter?: SceneCatalogReferenceFilter,
): SceneCatalogReferenceItem[] {
  const items: SceneCatalogReferenceItem[] = [];

  // ── Animations ─────────────────────────────────────────────────────────
  const validCustomAnimations = filterValidSceneAnimationEntries(
    catalog?.animations ?? [],
  );
  const customNamesLower = new Set(
    validCustomAnimations.map((a) => a.name.trim().toLocaleLowerCase()),
  );
  const sceneDico = (dico().behavior as Record<string, unknown>)
    .sceneCatalog as
    | (Record<string, unknown> & {
        defaultAnimations?: Record<string, string>;
      })
    | undefined;
  const defaultAnimationLabels = sceneDico?.defaultAnimations ?? {};

  for (const presetName of DEFAULT_SCENE_ANIMATION_NAMES) {
    if (customNamesLower.has(presetName.toLocaleLowerCase())) continue;
    items.push(
      makeAnimPlayItem(
        `preset-anim-${presetName}`,
        defaultAnimationLabels[presetName] ?? presetName,
        presetName,
      ),
    );
  }
  for (const entry of validCustomAnimations) {
    items.push(
      makeAnimPlayItem(`catalog-anim-${entry.id}`, entry.name, entry.name),
    );
  }

  // ── Events (publishable only: eventOutput wired) ───────────────────────
  for (const entry of catalog?.customEvents ?? []) {
    if (
      filter?.excludeCustomEventId &&
      entry.id === filter.excludeCustomEventId
    ) {
      continue;
    }
    if (!isCustomEventTriggerGraphPublishable(entry.triggerGraph)) continue;
    const tmpl: CustomSceneEventNodeTemplate = {
      type: "event",
      kind: "onCustomSceneEvent",
      eventId: entry.id,
    };
    items.push({
      id: `catalog-event-${entry.id}`,
      label: entry.name,
      section: "events",
      dotType: "event",
      template: tmpl,
    });
  }

  // ── Functions ──────────────────────────────────────────────────────────
  for (const entry of catalog?.functions ?? []) {
    if (!isFunctionGraphPublishable(entry.graph)) continue;
    const tmpl: FunctionCallNodeTemplate = {
      type: "action",
      kind: "call",
      functionId: entry.id,
    };
    items.push({
      id: `catalog-func-${entry.id}`,
      label: entry.name,
      section: "functions",
      dotType: "action",
      template: tmpl,
    });
  }

  // ── Variables ──────────────────────────────────────────────────────────
  for (const entry of catalog?.variables ?? []) {
    const tmpl: VariableRefNodeTemplate = {
      type: "variable",
      valueType: "variableRef",
      variableId: entry.id,
    };
    items.push({
      id: `catalog-var-${entry.id}`,
      label: entry.name,
      section: "variables",
      dotType: entry.outputValueType,
      template: tmpl,
    });

    if (isVariableGraphPublishable(entry.graph) && !filter?.valueOnly) {
      const setTmpl: SetVariableNodeTemplate = {
        type: "action",
        kind: "setVariable",
        variableId: entry.id,
      };
      const behaviorDico = dico().behavior as Record<string, unknown>;
      const actionsDico = behaviorDico.actions as
        | Record<string, string>
        | undefined;
      const setVarPrefix = actionsDico?.setVariablePrefix ?? "Set";
      items.push({
        id: `catalog-setvar-${entry.id}`,
        label: `${setVarPrefix} ${entry.name}`,
        section: "variables",
        dotType: "action",
        template: setTmpl,
      });
    }
  }

  for (const entry of catalog?.objectTypes ?? []) {
    if (
      !catalog ||
      sceneObjectTypeError(entry, catalog) ||
      entry.id === filter?.editingObjectTypeId
    )
      continue;
    items.push({
      id: `catalog-object-${entry.id}`,
      label: entry.name,
      section: "objects",
      dotType: "object",
      template: {
        type: "operator",
        operator: "constructObject",
        mode: "unary",
        inputValueTypes: ["object"],
        outputValueType: "object",
        objectTypeId: entry.id,
      },
    });
  }
  const editing = catalog?.objectTypes?.find(
    (entry) => entry.id === filter?.editingObjectTypeId,
  );
  if (editing && catalog)
    for (const extension of sceneObjectExtensions(editing)) {
      const rawName = sceneObjectTypeName(catalog, extension.typeId);
      const sourceLabels = dico().licensedPortal.layoutEditor
        .dataSources as Record<string, string>;
      items.push({
        id: "object-extension:" + extension.portId,
        label:
          (
            dico().behavior.sceneCatalog.objectEditor.nativeTypes as Record<
              string,
              string
            >
          )[rawName] ??
          sourceLabels[rawName] ??
          rawName,
        section: "variables",
        dotType: "object",
        template: {
          type: "variable",
          valueType: "object",
          objectSource: objectBaseSource(editing.id, extension.portId),
        },
      });
    }
  return items;
}

/**
 * Maps scene catalog reference entries to template-catalog items for the port
 * connection picker (same entries as the right-hand scene catalog panel).
 */
export function getSceneCatalogTemplateCatalogItems(
  catalog: SceneBehaviorCatalog | undefined,
  visibleSections?: SceneCatalogReferenceSection[],
  filter?: SceneCatalogReferenceFilter,
): BehaviorTemplateCatalogItem[] {
  const sceneDico = (dico().behavior as Record<string, unknown>)
    .sceneCatalog as Record<string, string> | undefined;
  const panelTitle = sceneDico?.panelTitle ?? "";
  const sectionLabels: Record<SceneCatalogReferenceSection, string> = {
    animations: sceneDico?.panelAnimations ?? "",
    events: sceneDico?.panelEvents ?? "",
    functions: sceneDico?.panelFunctions ?? "",
    variables: sceneDico?.panelVariables ?? "",
    objects: sceneDico?.panelObjects ?? "",
  };
  const allowedSections = visibleSections ?? [
    "animations",
    "events",
    "functions",
    "variables",
    "objects",
  ];

  const mapCatalogSection = (
    section: SceneCatalogReferenceSection,
  ): BehaviorTemplateCatalogSection => {
    if (section === "events") return "events";
    if (section === "variables" || section === "objects") return "variables";
    return "actions";
  };

  return getSceneCatalogReferenceCatalog(catalog, filter)
    .filter((item) => allowedSections.includes(item.section))
    .map((item) => ({
      id: `scene-catalog:${item.id}`,
      label: item.label,
      section: mapCatalogSection(item.section),
      categoryLabel: panelTitle,
      subCategoryLabel: sectionLabels[item.section],
      dotColor: getValueTypeColor(
        item.dotType as Parameters<typeof getValueTypeColor>[0],
      ),
      template: item.template,
      isConnectableTarget: true,
    }));
}

/**
 * Resolves a scene catalog detail route for a behavior node that references
 * a catalog entry (custom event, function, variable, or custom animation).
 *
 * @param node - Behavior node from the canvas.
 * @param catalog - Scene catalog used to validate entry IDs.
 * @returns A detail route when the node maps to a catalog entry, otherwise null.
 */
export function resolveSceneCatalogDetailRoute(
  node: BehaviorNode,
  catalog: SceneBehaviorCatalog | undefined,
): SceneBehaviorRoute | null {
  if (!catalog) return null;
  if (
    node.type === "operator" &&
    node.operator === "constructObject" &&
    !node.objectDefinition &&
    node.objectTypeId &&
    catalog.objectTypes?.some((t) => t.id === node.objectTypeId)
  ) {
    return { level: "detail", section: "objects", entryId: node.objectTypeId };
  }

  if (
    node.type === "event" &&
    node.kind === "onCustomSceneEvent" &&
    node.customEventId &&
    catalog.customEvents.some((entry) => entry.id === node.customEventId)
  ) {
    return {
      level: "detail",
      section: "events",
      entryId: node.customEventId,
    };
  }

  if (
    node.type === "action" &&
    node.kind === "call" &&
    node.functionId &&
    catalog.functions.some((entry) => entry.id === node.functionId)
  ) {
    return {
      level: "detail",
      section: "functions",
      entryId: node.functionId,
    };
  }

  if (
    node.type === "variable" &&
    "variableId" in node &&
    node.variableId &&
    catalog.variables.some((entry) => entry.id === node.variableId)
  ) {
    return {
      level: "detail",
      section: "variables",
      entryId: node.variableId,
    };
  }

  if (
    node.type === "action" &&
    node.kind === "setVariable" &&
    "variableId" in node &&
    node.variableId &&
    catalog.variables.some((entry) => entry.id === node.variableId)
  ) {
    return {
      level: "detail",
      section: "variables",
      entryId: node.variableId,
    };
  }

  if (node.type === "action" && node.kind === "animPlay") {
    const animationName = String(
      node.ports.find((port) => port.id === "name")?.value ?? "",
    ).trim();
    if (!animationName) return null;

    const customAnimation = catalog.animations.find(
      (entry) =>
        entry.name.trim().toLocaleLowerCase() ===
        animationName.toLocaleLowerCase(),
    );
    if (!customAnimation) return null;

    return {
      level: "detail",
      section: "animations",
      entryId: customAnimation.id,
    };
  }

  return null;
}

function makeAnimPlayItem(
  id: string,
  label: string,
  animationName: string,
): SceneCatalogReferenceItem {
  return {
    id,
    label,
    section: "animations",
    dotType: "action",
    template: {
      type: "action",
      kind: "animPlay",
      ports: [
        {
          id: "name",
          direction: "input",
          valueType: "string",
          value: animationName,
        },
      ],
    } as unknown as BehaviorDraggedNodeTemplate,
  };
}
